import useFirstRender from '@klv/hooks/useFirstRender';
import useSlides from '@klv/hooks/useSlides';
import { generateClassName, getColorScheme, hasImage } from '@klv/shared/utils/functions';
import { getBackgroundCoreColor, getHeadlineClassNames } from './utils';
import { ModulesGrid } from '@klv/utils/grid';
import Buttons from '@klv/molecules/buttons';
import Eyebrow from '@klv/atoms/eyebrow';
import Icon from '@klv/atoms/icon';
import Image from '@klv/atoms/image';

import * as s from './styles/socialProof.module.css';

import type { ISocialProof, TSocialProofSlide } from './types';

const SocialProof = ({
  mainBackground = 'light',
  slides,
}: ISocialProof) => {
  const {
    slides: rows, slideState, swipeable, swipe,
  } = useSlides(slides, ['left', 'right']);

  const firstRender = useFirstRender();

  return (
    <div
      {...swipeable}
      className={generateClassName([s.socialProof, 'mdPaddingTop', 'mdPaddingBottom'])}
      data-color-scheme={getColorScheme(mainBackground)}
    >
      <div className={s.wrapper}>
        <ModulesGrid className={s.grid}>
          {rows.map((slide: TSocialProofSlide) => (
            <div
              style={{
                '--color-copyContent-background': `var(--color-core-${getBackgroundCoreColor(slide.copyContentBackground)})`,
              } as React.CSSProperties}
              className={generateClassName([s.copyContent, slide.$current && s.current])}
              key={slide.headlineText}
            >
              {slide.headlineText && (
                <div className={generateClassName([s.headlineText, ...getHeadlineClassNames(slide.headlineStyle)])}>
                  {slide.headlineText}
                </div>
              )}          
              <div className={s.spacer}>
                {slide?.eyebrowText && <Eyebrow textColor="white" content={slide.eyebrowText} />}
              </div>
              {slide?.buttons &&
                <Buttons buttons={slide.buttons} variants={['ghost']} buttonColor="light" {...slide.buttons} />}
            </div>
          ))}
          <div className={s.imageWrapper}>
            {rows.map((slide: TSocialProofSlide) => (
              <div
                className={generateClassName([
                  s.imageSlider,
                  firstRender && s.firstRender,
                  slide.$current && s.current,
                  slide.$previous && s.previous,
                  slide.$dir === 'left' ? s.left : s.right,
                ])}
                key={slide.headlineText}
              >
                {hasImage(slide.image) && (
                <Image
                  className={s.image}
                  altText={slide.image?.altText}
                  imageData={slide.image.gatsbyImage}
                />
                )}
              </div>
            ))}
          </div>
          <div className={s.slideButtonContainer}>
            <div>
              <button className={s.slideButton} aria-label="Previous Slide" onClick={() => swipe(-1)}>
                <Icon iconName="LongArrowLeft" fillColor={'white'} />
              </button>
              <button className={s.slideButton} aria-label="Next Slide" onClick={() => swipe(1)}>
                <Icon iconName="LongArrowRight" fillColor={'white'} />
              </button>
            </div>
            <div className={s.progressContainer}>
              {`${slideState.current + 1}/${slides.length}`}
            </div>
          </div>
        </ModulesGrid>
      </div>
    </div>
  );
};

export default SocialProof;
